import { FC, Fragment } from "react";
import { Route } from "nextjs-routes";
import { gql } from "@apollo/client";
import NextLink from "next/link";

import Avatar from "@src/lib/ui/Avatar";
import { ActiveClientFragment } from "./ActiveClientsAvatarStack.generated";
import { cn } from "@src/lib/utils/cn";

type Props = {
  clients: ActiveClientFragment[];
  size?: number;
  // Optional per-client navigation route. Domains that can map a
  // remote editor to a specific URL (course → prompt screen,
  // module → membership) supply this; domains that can't (workflow)
  // omit it and the avatar renders as a static badge.
  getRoute?: (client: ActiveClientFragment) => Route | undefined;
};

const ActiveClientsAvatarStack: FC<Props> = ({
  clients,
  size = 32,
  getRoute,
}) => {
  if (clients.length === 0) return null;
  return (
    <span className="flex">
      {clients.map((client, i) => {
        const route = getRoute?.(client);
        const avatar = (
          <Avatar
            person={client.user}
            size={size}
            showTooltip
            className={cn(
              "shadow-[0_0_0_2px_white]",
              i > 0 && "-ml-2.5",
              route && "cursor-pointer",
            )}
          />
        );
        return route ? (
          <NextLink key={client.clientId} href={route}>
            {avatar}
          </NextLink>
        ) : (
          <Fragment key={client.clientId}>{avatar}</Fragment>
        );
      })}
    </span>
  );
};

export default ActiveClientsAvatarStack;

gql`
  fragment ActiveClient on ActiveClient {
    promptUuid
    courseVersionId
    moduleVersionId
    skillVersionId
    verificationStepId
    moduleContentMembershipId
    richTextDocumentId
    workflowVersionId
    clientId
    user {
      id
      name
      profileImage {
        ...AvatarProfileImage
      }
    }
  }
`;
